import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { Subscription, SubscriptionLine, LineAmendment, HolidayRequest, CutoffScenario, PriceListItem } from '@/types'
import subscriptionService from '@/services/subscriptionService'
import { isUnknownOutcome } from '@/services/apiClient'
import holidayService from '@/services/holidayService'
import priceListService from '@/services/priceListService'
import { billingFrequencyKey, frequenciesFromKey } from '@/utils/billingFrequency'
import {
  MdDeleteOutline,
  MdInfoOutline,
  MdPauseCircleOutline,
  MdTune,
  MdUndo,
} from 'react-icons/md'
import { formatDayAndMonth as shortDate } from '@/utils/dateFormat'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import { clampOpsToLine, planAmendments, type AmendmentInterval } from '@/utils/amendmentPlan'
import type { ScheduledChange } from '@/utils/weeklyOrder'
import SubscriptionProductCard, { type ProductAnnotation } from '@/components/sun/SubscriptionProductCard'
import {
  lastDeliveryDateFor,
  neverDelivers,
  nextDeliveryDateFor,
  standingQuantityFor,
  changeFromTheStart,
  isDeliveryWeek,
} from '@/utils/deliverySchedule'
import { toLocalDateString } from '@/utils/deliveryDays'
import { useAuth } from '@/hooks/useAuth'
import { useCutoffs } from '@/hooks/useCutoffs'
import CutoffMessage from './common/CutoffMessage'
import {
  getDateDaysFromNow,
  formatDate,
  getDayName,
  getWeekdayFromDateString,
  addDaysToDateString,
  formatOrdinalDate,
  getChargebeeIdForDay,
} from '@/utils/deliveryDays'
import '@/styles/components/change-items-modal.css'
import '@/styles/components/add-product-modal.css'
import deliveryOverlayService from '@/services/deliveryOverlayService'
import { movedDateNote, movedDeliveryTargets, schedulePeriods } from '@/utils/deliveryOverlay'
import { formatDate as longDate } from '@/utils/dateFormat'

// Matches backend WorkingDaysLeadTime validator output, so a cutoff error
// returned on submit can be swapped for the friendly CutoffMessage banner.
const isCutoffErrorMessage = (msg: string | null | undefined): boolean => {
  if (!msg) return false
  return /must be on or after/i.test(msg)
      || /selected date is too early/i.test(msg)
      || /earliest allowed date/i.test(msg)
}

interface ChangeItemsModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void | Promise<void>
  currentItems: SubscriptionLine[]
  /** Fallback subscription for a new line when the product's own can't be found. */
  subscriptionId: string
  /**
   * The customer's active subscriptions. A product added to another day goes
   * on the subscription it is already on (DF-382), priced from that
   * subscription's billing frequency.
   */
  subscriptions?: Subscription[]
  /** The weekday (0=Sun..6=Sat) of the delivery card this was opened from. */
  deliveryDay: number
  /** The delivery card's date (YYYY-MM-DD); used as the preferred start date. */
  defaultEffectiveDate?: string
  /**
   * Scheduled changes per product id, for the annotations on the product
   * step. Optional: the Products page has no order panel to draw them from,
   * and a missing map simply means no annotations rather than a crash.
   */
  scheduledChangesByProduct?: Map<string, ScheduledChange[]>
  /** Open the product detail from the chooser. Optional: callers without a
   *  detail modal simply have non-opening packshots. */
  onViewProduct?: (productId: string, name: string, unitPrice: number) => void
  amendments?: LineAmendment[]
  /**
   * What is already scheduled on this product, in the same words the weekly
   * panel uses. Built by the dashboard from the shared builder rather than
   * worked out again here, so the card and this dialog cannot disagree.
   */
  scheduledChanges?: ScheduledChange[]
  /** Undo one of them, handed back to the dashboard's own confirmation. */
  onCancelChange?: (change: ScheduledChange) => void
  /**
   * Open straight onto a product, skipping the picker. sunMode passes this
   * because the order row the customer clicked already *is* step 1.
   */
  initialProductId?: string
  /**
   * Launch straight into the "when" step with the change already chosen. The
   * Delivery Management cards do the what (new quantity, pause, remove) inline,
   * so only the date is left for the dialog. Requires initialProductId.
   */
  initialIntent?: Intent
  /** New quantity per delivery day, for a card-initiated quantity change. */
  initialQuantities?: Record<number, number>
}

/**
 * What the customer currently gets of a product, in a sentence.
 *
 * The standing order is the baseline every scheduled change is a departure
 * from, so it is worth stating on every row rather than only where something
 * has been booked. Days are named in full because the row has the width, and
 * "every Monday and Thursday" reads as a routine in a way "Mon, Thu" does not.
 */
const describeStandingOrder = (product: {
  linesByDay: Map<number, { quantity: number }>
}): string | null => {
  const days = Array.from(product.linesByDay.keys()).sort((a, b) => a - b)

  if (days.length === 0) return null

  const quantities = days.map(d => product.linesByDay.get(d)?.quantity ?? 0)
  const sameEveryDay = quantities.every(q => q === quantities[0])

  // Singular after "every": "every Monday and Thursday", not "every Mondays".
  const list = (labels: string[]): string => (labels.length < 2
    ? labels[0]
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`)

  // "on Mondays and Thursdays", the same shape the scheduled-change lines use,
  // so the standing line and the changes beneath it read in one voice.
  if (sameEveryDay) {
    return `You get ${quantities[0]} on ${list(days.map(d => `${getDayName(d)}s`))}`
  }

  // Per-day where the quantities differ, since "1 on Mondays and Thursdays"
  // would be untrue when Thursday is 2.
  return `You get ${list(days.map(d => `${product.linesByDay.get(d)?.quantity ?? 0} on ${getDayName(d)}s`))}`
}

// How far ahead to offer change dates per day. Roughly six months, so both the
// start and the end date can be set well beyond the next delivery cycle.
const HORIZON_WEEKS = 26
const HORIZON_DAYS = HORIZON_WEEKS * 7

type Step = 'product' | 'intent' | 'quantity' | 'dates'

/**
 * What the customer wants to do. All three end up writing subscription lines;
 * they differ in whether a quantity is involved and which dates are asked for.
 *
 * A pause is deliberately NOT a quantity-0 amendment: CRM's tsg_quantity has a
 * minimum of 1 on both amendments and lines, so zero cannot be stored at all.
 * It is expressed instead as ending the line before the gap and starting a new
 * one after it — operations the portal already relies on.
 */
/**
 * Every save from this dialog checks its own outcome when a request times out
 * or drops (DF-385), so the global "couldn't save" overlay stays out of it.
 */
const CHECKS_OUTCOME = { checksUnknownOutcome: true }

type Intent = 'quantity' | 'pause' | 'remove'

/** One of the customer's products, with the line it has on each delivery day. */
interface EditableProduct {
  productId: string
  name: string
  image: string | null
  unitPrice: number | null
  /** The line that governs each day now: the one delivering soonest. */
  linesByDay: Map<number, SubscriptionLine>
  /** Every line on the day. A pause leaves two: the one it ended and the one
   *  scheduled to follow it, and taking either at random targeted the wrong
   *  one when writing the change. */
  allLinesByDay: Map<number, SubscriptionLine[]>
}

/**
 * Edit-your-delivery wizard (FEDEV-553-358), mirroring AddProductModal:
 *   1. Choose which product to change.
 *   2. Set a new quantity per delivery day that product is on.
 *   3. Choose when the change starts and when it ends.
 *
 * Each changed day is submitted as its own line amendment, so each carries its
 * own effective_from (that day's next delivery) and effective_to (that day's
 * last delivery on or before the shared end date). Days therefore stop on
 * their own delivery dates rather than all stopping on one shared date.
 *
 * Taking a quantity to 0 is a permanent removal, not a pause: the backend ends
 * the line outright and ignores any end date. The end control is locked to
 * "until further notice" in that case so the dialog can't promise something it
 * won't do.
 */
const ChangeItemsModal: React.FC<ChangeItemsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentItems,
  subscriptionId,
  subscriptions = [],
  deliveryDay,
  defaultEffectiveDate,
  scheduledChangesByProduct,
  onViewProduct,
  amendments = [],
  scheduledChanges = [],
  onCancelChange,
  initialProductId,
  initialIntent,
  initialQuantities,
}) => {
  const { cutoffs } = useCutoffs()
  const { user } = useAuth()

  const [step, setStep] = useState<Step>('product')
  /** What the customer came here to do; picked before anything else. */
  const [intent, setIntent] = useState<Intent>('quantity')
  /** For a pause: the last delivery that is skipped. */
  const [pauseUntil, setPauseUntil] = useState<string>('')
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  /** New quantity per delivery day for the selected product. */
  const [quantities, setQuantities] = useState<Record<number, number>>({})
  /** Which of the product's days a pause or removal applies to; all by default. */
  const [selectedDays, setSelectedDays] = useState<number[]>([])
  const [changeFrom, setChangeFrom] = useState<string>('')
  /** DF-478: where moved deliveries (Christmas) actually arrive, and the periods. */
  const [moves, setMoves] = useState<{ targets: Map<string, string>, periods: ReturnType<typeof schedulePeriods> }>({ targets: new Map(), periods: [] })
  /** How many weeks the change runs for. null = until further notice. */
  /** For a quantity change: the last date it applies to. '' = until further notice. */
  const [changeUntil, setChangeUntil] = useState<string>('')
  const [holidays, setHolidays] = useState<HolidayRequest[]>([])
  /** Needed to offer a day the product isn't on yet: only the price list knows
   *  whether it can be delivered then, and its per-day ChargeBee id. */
  const [priceListItems, setPriceListItems] = useState<PriceListItem[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // BUG-3 (backend docs/bugs.md): setting a quantity to 0 REMOVES the product
  // from all future deliveries; customers use it expecting to pause one
  // delivery. The first Save click with removals shows an explicit
  // confirmation and signposts Skip; the second click submits.
  const [confirmingRemovals, setConfirmingRemovals] = useState(false)

  // Ref on the scrollable content so a submit failure can scroll back to the
  // error banner.
  const modalContentRef = useRef<HTMLDivElement>(null)

  const cardWeekday = defaultEffectiveDate ? getWeekdayFromDateString(defaultEffectiveDate) : deliveryDay

  /**
   * Every pending change on a line, minus the one folded into its standing
   * quantity. CRM allows several per line, so taking only the first hid the
   * rest; and the opening change on a not-yet-started line is already what the
   * "Currently N" figure says, so listing it again as pending contradicts it.
   */
  const pendingForLine = useCallback((line: SubscriptionLine): LineAmendment[] => {
    const folded = changeFromTheStart(line, amendments)

    return amendments.filter(a => a.subscription_line_id === line.id
      && a.amendment_id !== folded?.amendment_id)
  }, [amendments])

  /**
   * Every pending amendment on a line, not just the first. CRM allows several
   * and does not stop them overlapping, so the merge planner needs the lot to
   * work out what the line should end up carrying.
   */
  const intervalsForLine = useCallback((lineId: string): AmendmentInterval[] => {
    return amendments
      .filter(a => a.subscription_line_id === lineId)
      .map(a => ({
        id: a.amendment_id,
        quantity: a.new_quantity,
        from: a.effective_from.slice(0, 10),
        to: a.effective_to ? a.effective_to.slice(0, 10) : null,
      }))
  }, [amendments])

  // Every line still live: not cancelled, not already finished, and not one of
  // the empty-range rows a cancelled-before-it-started line leaves behind.
  const activeLines = useMemo<SubscriptionLine[]>(() => {
    const today = getDateDaysFromNow(0)
    return currentItems.filter(line => {
      if (line.statecode === 1) return false
      if (neverDelivers(line)) return false
      const end = line.end_date?.slice(0, 10)
      if (end && end < today) return false
      return true
    })
  }, [currentItems])

  // The customer's products, each with its per-day lines. Ordered so the day
  // they opened the dialog from comes first.
  const products = useMemo<EditableProduct[]>(() => {
    const byProduct = new Map<string, EditableProduct>()
    for (const line of activeLines) {
      if (!line.product_id) continue
      let entry = byProduct.get(line.product_id)
      if (!entry) {
        entry = {
          productId: line.product_id,
          name: line.product_name || line.name || 'Product',
          image: line.product_image ?? null,
          unitPrice: line.unit_price_inc_vat ?? line.unit_price ?? null,
          linesByDay: new Map(),
          allLinesByDay: new Map(),
        }
        byProduct.set(line.product_id, entry)
      }
      if (line.delivery_day !== null && line.delivery_day !== undefined) {
        const forDay = entry.allLinesByDay.get(line.delivery_day) ?? []
        forDay.push(line)
        entry.allLinesByDay.set(line.delivery_day, forDay)
      }
    }

    // Pick the one line that speaks for each day, the one delivering soonest.
    // A paused product has two live lines on the same day, and taking whichever
    // happened to come last meant the change could be written to the line that
    // has already stopped, or bounded by the wrong line's dates.
    for (const entry of byProduct.values()) {
      for (const [day, lines] of entry.allLinesByDay) {
        const governing = [...lines].sort((a, b) => {
          const nextA = nextDeliveryDateFor(a)
          const nextB = nextDeliveryDateFor(b)
          if (nextA && nextB) return nextA.getTime() - nextB.getTime()
          if (nextA) return -1
          if (nextB) return 1
          return 0
        })[0]
        if (governing) entry.linesByDay.set(day, governing)
      }
    }

    return Array.from(byProduct.values()).sort((a, b) => {
      const aOnCard = a.linesByDay.has(cardWeekday) ? 0 : 1
      const bOnCard = b.linesByDay.has(cardWeekday) ? 0 : 1
      return aOnCard - bOnCard || a.name.localeCompare(b.name)
    })
  }, [activeLines, cardWeekday])

  const selectedProduct = useMemo<EditableProduct | null>(
    () => products.find(p => p.productId === selectedProductId) ?? null,
    [products, selectedProductId]
  )

  /** The days this product is delivered on, Monday first. */
  const productDeliveryDays = useMemo<number[]>(
    () => Array.from(selectedProduct?.linesByDay.keys() ?? [])
      .sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b)),
    [selectedProduct]
  )

  // A pause or removal starts on every day the product is on; the customer can
  // uncheck days when it is on more than one. Reset when the product changes.
  useEffect(() => {
    setSelectedDays(productDeliveryDays)
  }, [productDeliveryDays])

  const toggleDay = (day: number) => {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  }

  /**
   * The subscription the selected product is on (DF-382). A product added to
   * another day goes on that same subscription, not whichever one the page
   * happens to list first, so a fortnightly product stays fortnightly.
   */
  const productSubscriptionId = useMemo<string>(() => {
    const line = selectedProduct ? Array.from(selectedProduct.linesByDay.values())[0] : undefined
    const own = line && subscriptions.find(s => s.external_id === line.subscription_external_id)
    return own?.id ?? subscriptionId
  }, [selectedProduct, subscriptions, subscriptionId])

  /** The selected product's entry in the price list, for day availability. */
  const priceItem = useMemo<PriceListItem | null>(
    () => priceListItems.find(i => i.product_id === selectedProductId) ?? null,
    [priceListItems, selectedProductId]
  )

  /** The customer's delivery days, from CRM with the current lines as a fallback. */
  const customerDeliveryDays = useMemo<number[]>(() => {
    if (user?.delivery_days && user.delivery_days.length > 0) {
      return [...user.delivery_days]
    }
    const days = new Set<number>()
    activeLines.forEach(l => {
      if (l.delivery_day !== null && l.delivery_day !== undefined) days.add(l.delivery_day)
    })
    return Array.from(days)
  }, [user?.delivery_days, activeLines])

  /**
   * Every day worth offering: the ones this product is already on, plus the
   * customer's other delivery days where it *could* be delivered. Showing only
   * the existing days meant a product on Thursday could never be added to
   * Monday from here, which read as the dialog having lost a day.
   */
  const productDays = useMemo<number[]>(() => {
    if (!selectedProduct) return []
    const days = new Set<number>(selectedProduct.linesByDay.keys())
    if (priceItem) {
      customerDeliveryDays.forEach(day => {
        if (getChargebeeIdForDay(priceItem, day) !== null) days.add(day)
      })
    }
    return Array.from(days).sort((a, b) => a - b)
  }, [selectedProduct, priceItem, customerDeliveryDays])

  /**
   * Named when the product cannot reach every day this customer takes: not all
   * products are made for every round, and without saying so the missing day
   * looks like an omission rather than a rule.
   */
  const restrictedToDays = useMemo<string | null>(() => {
    if (!priceItem || productDays.length === 0) return null
    if (productDays.length >= customerDeliveryDays.length) return null

    const names = productDays.map(day => `${getDayName(day)}s`)

    return names.length < 2
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  }, [priceItem, productDays, customerDeliveryDays])

  /** Does this product already have a line on this day? */
  const hasLineOn = useCallback(
    (day: number): boolean => !!selectedProduct?.linesByDay.get(day),
    [selectedProduct]
  )

  /**
   * The quantity a day starts on: any pending amendment, else the CRM value.
   * This is also the baseline for "has it changed", so reverting to the CRM
   * value still submits, which is what triggers the backend's delete-on-revert
   * and clears the pending amendment.
   */
  /**
   * What the customer normally gets on this day, matching the card that opened
   * this dialog. Pending changes are deliberately NOT applied: they are
   * deviations, already shown as annotations, and the merge planner folds this
   * edit into them on submit. Applying them here made a card reading "Mon 1"
   * open an editor headed "Mon 2", and made the summary say "3 instead of 2"
   * where 2 was a future value rather than what is delivered today.
   */
  const currentQuantityForDay = useCallback((day: number): number => {
    const line = selectedProduct?.linesByDay.get(day)
    if (!line) return 0

    return standingQuantityFor(line, amendments)
  }, [selectedProduct, amendments])

  /**
   * Every billing frequency the customer's subscriptions run at. A product is
   * only in the price list for its own frequency (DF-382), so a fortnightly
   * product needs the fortnightly list to be found at all.
   */
  // Keyed on the values, not the array: the subscriptions are refetched after
  // every save, and a new array here would re-run the open effect below and
  // reset the dialog mid-save.
  const frequencyKey = useMemo(() => billingFrequencyKey(subscriptions), [subscriptions])
  const frequencies = useMemo(() => frequenciesFromKey(frequencyKey), [frequencyKey])

  const fetchData = useCallback(async () => {
    try {
      const [holidayResp, items] = await Promise.all([
        holidayService.getHolidays(100),
        priceListService.getPriceListItemsForFrequencies(frequencies),
      ])
      setHolidays(holidayResp.data || [])
      setPriceListItems(items)
    } catch (err) {
      console.error('Failed to fetch dialog data:', err)
    }
  }, [frequencies])

  // Reset to a clean state every time the dialog opens. When the caller named
  // a product (sunMode's order row), start on the quantity step instead: the
  // customer has already chosen, so asking again would be a dead step.
  useEffect(() => {
    if (isOpen) {
      // Card-initiated: the what is already chosen, so open on the dates step.
      if (initialIntent && initialProductId) {
        setStep('dates')
        setIntent(initialIntent)
        setSelectedProductId(initialProductId)
        setQuantities(initialQuantities ?? {})
      } else {
        setStep(initialProductId ? 'intent' : 'product')
        setIntent('quantity')
        setSelectedProductId(initialProductId ?? null)
        setQuantities({})
      }
      setPauseUntil('')
      setChangeFrom('')
      setChangeUntil('')
      setSubmitting(false)
      setError(null)
      setConfirmingRemovals(false)
      fetchData()
      // Supporting detail only: without it the moved-date message doesn't show.
      deliveryOverlayService.getOverlay()
        .then(o => {
          const targets = movedDeliveryTargets(o.round_day_amendments, o.date_overrides)
          setMoves({ targets, periods: schedulePeriods(targets) })
        })
        .catch(() => undefined)
    }
  }, [isOpen, fetchData, initialProductId, initialIntent, initialQuantities])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    let releaseScroll: (() => void) | undefined
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      releaseScroll = lockBodyScroll()
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      releaseScroll?.()
    }
  }, [isOpen, onClose])

  const isDateOnHoliday = useCallback((dateString: string): boolean => {
    const date = new Date(dateString)
    return holidays.some(h => {
      if (h.statecode !== 0) return false
      return date >= new Date(h.start_date) && date <= new Date(h.end_date)
    })
  }, [holidays])

  /** The days whose quantity the customer has actually altered. */
  const changedDays = useMemo<number[]>(
    () => productDays.filter(day => (quantities[day] ?? currentQuantityForDay(day)) !== currentQuantityForDay(day)),
    [productDays, quantities, currentQuantityForDay]
  )

  const quantityForDay = useCallback(
    (day: number): number => quantities[day] ?? currentQuantityForDay(day),
    [quantities, currentQuantityForDay]
  )

  /** Days being taken to zero, i.e. removed from all future deliveries. */
  const removedDays = useMemo<number[]>(
    () => changedDays.filter(day => quantityForDay(day) === 0),
    [changedDays, quantityForDay]
  )

  /**
   * Every day the product is delivered on has been taken to 0. That is not
   * really a quantity change: the customer wants the product to stop, and the
   * only question is whether it comes back. Asking beats guessing, because
   * pausing and removing are very different things to get wrong.
   */
  const zeroingEverything = useMemo<boolean>(() => {
    // Something has to be going away...
    const deliversNow = productDays.some(day => currentQuantityForDay(day) > 0)

    // ...and nothing may be left at the end of it. Checking only the days that
    // deliver today was wrong: taking Monday to 0 while putting 1 on Thursday
    // is moving the delivery, not stopping it, and the prompt appeared anyway.
    return deliversNow && productDays.every(day => quantityForDay(day) === 0)
  }, [productDays, currentQuantityForDay, quantityForDay])

  // A removal is permanent and the backend ignores an end date on it, so the
  // end date is forced off while any day is being removed.
  const durationLocked = removedDays.length > 0

  // Which cutoff applies. With a mix of directions the strictest wins, because
  // one start date covers every day in this change.
  /**
   * Which cutoffs apply. A single change can now both amend a line and create
   * one, and each has its own lead time, so the latest minimum wins rather
   * than picking one scenario.
   */
  const activeScenarios = useMemo((): CutoffScenario[] => {
    const list: CutoffScenario[] = []
    if (changedDays.some(d => !hasLineOn(d) && quantityForDay(d) > 0)) list.push('add_new_line')
    if (changedDays.some(d => hasLineOn(d) && quantityForDay(d) > currentQuantityForDay(d))) list.push('increase')
    if (removedDays.length > 0) list.push('remove_line')
    if (changedDays.some(d => hasLineOn(d) && quantityForDay(d) > 0 && quantityForDay(d) < currentQuantityForDay(d))) {
      list.push('decrease')
    }
    return list.length > 0 ? list : ['decrease']
  }, [changedDays, removedDays, hasLineOn, quantityForDay, currentQuantityForDay])

  const minDate = useMemo((): string => {
    if (!cutoffs) return getDateDaysFromNow(1)
    // ISO dates sort lexicographically, so the last is the latest.
    const sorted = activeScenarios
      .map(scenario => cutoffs.cutoffs[scenario].min_date)
      .sort()
    return sorted[sorted.length - 1] ?? getDateDaysFromNow(1)
  }, [cutoffs, activeScenarios])

  // Per-day list of valid change dates: that weekday, from the cutoff onwards,
  // skipping booked holidays. A non-weekly product only delivers on its
  // subscription's delivery weeks, so only those are offered (DF-380).
  const availableDatesByDay = useMemo<Record<number, string[]>>(() => {
    const map: Record<number, string[]> = {}
    const productSubscription = subscriptions.find(s => s.id === productSubscriptionId)
    for (const day of productDays) {
      // A change outside the line's own delivery window could never take
      // effect, so those dates aren't offered at all. That applies at both
      // ends: after its last delivery, and before its first, which matters for
      // a product added to start weeks from now.
      const line = selectedProduct?.linesByDay.get(day)
      const lastDelivery = line ? lastDeliveryDateFor(line) : null
      const cutoff = lastDelivery ? toLocalDateString(lastDelivery) : null
      const floor = line?.start_date ? line.start_date.slice(0, 10) : null

      const dates: string[] = []
      for (let i = 0; i < HORIZON_DAYS; i++) {
        const d = addDaysToDateString(minDate, i)
        if (getWeekdayFromDateString(d) !== day) continue
        if (isDateOnHoliday(d)) continue
        if (!isDeliveryWeek(d, productSubscription?.start_date, productSubscription?.billing_frequency)) continue
        if (floor && d < floor) continue
        if (cutoff && d > cutoff) break
        dates.push(d)
      }
      map[day] = dates
    }
    return map
  }, [productDays, minDate, isDateOnHoliday, selectedProduct, subscriptions, productSubscriptionId])

  /**
   * Why no dates are offered after a product's end (DF-387). Dates past its
   * last delivery are left out of the picker, so without this the customer
   * can't tell why. Names the days when only some of them are ending.
   */
  const endNote = useMemo<string | null>(() => {
    if (!selectedProduct) return null
    const lines = Array.from(selectedProduct.linesByDay.values()).filter(line => !neverDelivers(line))
    const ending = lines
      .map(line => ({ day: line.delivery_day, last: lastDeliveryDateFor(line) }))
      .filter((e): e is { day: number, last: Date } => e.last !== null && e.day !== null && e.day !== undefined)
    if (ending.length === 0) return null

    const last = ending.reduce((a, b) => (b.last > a.last ? b : a)).last
    const subject = ending.length === lines.length
      ? 'This product ends'
      : `Your ${ending.map(e => `${getDayName(e.day)}`).join(' and ')} delivery of this product ends`

    return `${subject} on ${formatOrdinalDate(toLocalDateString(last))}, so you can't change or pause it after that. `
      + 'To keep it going, cancel its removal under "Already scheduled changes" first.'
  }, [selectedProduct])

  // The shared "change from" options: the union across the days being changed.
  const unionDates = useMemo<string[]>(() => {
    const set = new Set<string>()
    // A pause or a removal acts on the lines that exist, so its dates come from
    // the days the product is actually delivered on. A quantity change can add
    // a day the product isn't on yet, so before anything is edited it offers
    // every day it could be on.
    const days = intent === 'quantity'
      ? (changedDays.length > 0 ? changedDays : productDays)
      : selectedDays
    days.forEach(day => (availableDatesByDay[day] || []).forEach(d => set.add(d)))
    return Array.from(set).sort()
  }, [intent, changedDays, productDays, selectedDays, availableDatesByDay])

  // Explicit pick if still valid, else the card's own date, else the soonest.
  const effectiveChangeFrom = useMemo<string>(() => {
    if (changeFrom && unionDates.includes(changeFrom)) return changeFrom
    if (defaultEffectiveDate && unionDates.includes(defaultEffectiveDate)) return defaultEffectiveDate
    return unionDates[0] || ''
  }, [changeFrom, unionDates, defaultEffectiveDate])

  // True when the cutoff or a holiday pushed the change past the delivery the
  // customer clicked on, so the dialog can explain why the date differs.
  const changeDatePushed = !!defaultEffectiveDate
    && !!effectiveChangeFrom
    && effectiveChangeFrom !== defaultEffectiveDate

  /** End-date options, which must fall on or after the start. */
  const untilOptions = useMemo(
    () => unionDates.filter(d => d >= effectiveChangeFrom),
    [unionDates, effectiveChangeFrom]
  )

  // A pause has to end, so it defaults to the shortest one: a single skip.
  const effectivePauseUntil = pauseUntil && untilOptions.includes(pauseUntil)
    ? pauseUntil
    : (untilOptions[0] ?? '')

  // A quantity change need not end, so it defaults to until further notice.
  // Moving the start past a chosen end drops back to that default.
  const effectiveChangeUntil = durationLocked || !untilOptions.includes(changeUntil)
    ? ''
    : changeUntil

  // What each changed day actually gets: its own next delivery on or after the
  // shared start, and its own last changed delivery.
  const perDaySchedule = useMemo(() => {
    return changedDays.map(day => {
      const list = availableDatesByDay[day] || []
      // Never earlier than the chosen start (it used to fall back to the last
      // date before it). The list already reaches 26 weeks, so a day with
      // nothing on or after the start has no date, and the change is blocked.
      const first = list.find(d => d >= effectiveChangeFrom) || ''
      // The shared end date rarely falls on this day, so take this day's own
      // last delivery on or before it.
      const last = first && effectiveChangeUntil
        ? [...list].reverse().find(d => d >= first && d <= effectiveChangeUntil) || ''
        : ''
      return {
        day,
        quantity: quantityForDay(day),
        was: currentQuantityForDay(day),
        first,
        last,
        lineId: selectedProduct?.linesByDay.get(day)?.id,
        chargebeeId: priceItem ? getChargebeeIdForDay(priceItem, day) : null,
      }
    })
  }, [changedDays, availableDatesByDay, effectiveChangeFrom, effectiveChangeUntil, quantityForDay, currentQuantityForDay, selectedProduct, priceItem])

  // At least one line must survive across the whole subscription. The remove
  // flow has no per-day quantities, so it counts the selected days that carry a
  // line; the quantity flow counts the days taken to zero. Without covering the
  // remove flow, removing a product delivered on two days showed the "then it
  // stops" confirmation and only surfaced the block as a backend error
  // afterwards, which read as a contradiction (FEDEV-599, Laura's feedback).
  const linesBeingRemovedCount = intent === 'remove'
    ? selectedDays.filter(day => selectedProduct?.linesByDay.has(day)).length
    : removedDays.length
  const remainingLineCount = activeLines.length - linesBeingRemovedCount
  const wouldRemoveEverything = remainingLineCount <= 0

  const allChangedDaysHaveDates = perDaySchedule.length > 0 && perDaySchedule.every(s => !!s.first)

  const canSubmit = intent === 'quantity'
    ? allChangedDaysHaveDates && !wouldRemoveEverything
    : intent === 'pause'
      ? !!effectiveChangeFrom && !!effectivePauseUntil && selectedDays.length > 0
      : !!effectiveChangeFrom && selectedDays.length > 0 && !wouldRemoveEverything

  // Any quantity edit invalidates a shown removal confirmation.
  useEffect(() => {
    setConfirmingRemovals(false)
  }, [quantities])

  const handleSelectProduct = (product: EditableProduct) => {
    setSelectedProductId(product.productId)
    setQuantities({})
    setChangeFrom('')
    setChangeUntil('')
    setStep('intent')
  }

  const setDayQty = (day: number, qty: number) => {
    setQuantities(prev => ({ ...prev, [day]: Math.max(0, qty) }))
  }

  const handleSubmit = async () => {
    if (!selectedProduct) return
    if (intent === 'quantity' && perDaySchedule.length === 0) return

    if (wouldRemoveEverything) {
      setError('You’ll need to keep at least one item in your subscription. To stop deliveries temporarily, you can pause them in Delivery Management. To cancel your subscription, please call our Customer Service team on 0330 555 5588.')
      return
    }

    // Removals need an explicit confirmation first (BUG-3).
    if (intent === 'quantity' && removedDays.length > 0 && !confirmingRemovals) {
      setConfirmingRemovals(true)
      modalContentRef.current?.scrollTo({
        top: modalContentRef.current.scrollHeight,
        behavior: 'smooth',
      })
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      // Removal: end the product for good. Sent as a quantity-0 amendment (CRM
      // reads that as ending the line); the chosen date is the first delivery to
      // drop, and the backend ends the line the day before (FEDEV-613).
      if (intent === 'remove') {
        const lines = Array.from(selectedProduct.linesByDay.entries())
          .filter(([day, line]) => selectedDays.includes(day) && (!!line.external_id || !!line.id))

        await Promise.all(lines.map(async ([, line]) => {
          await subscriptionService.createLineAmendment({
            subscription_line_id: line.id,
            quantity: 0,
            effective_from: effectiveChangeFrom,
          }, CHECKS_OUTCOME)
        }))

        await onSuccess?.()
        onClose()
        return
      }

      // Temporary pause: a single Line Freeze per day (DF-350). The product
      // stops from the chosen date up to the day before it resumes, then comes
      // back on its own, so there is no longer an end-line-plus-resume-line pair
      // to keep in step or roll back.
      if (intent === 'pause') {
        const lines = Array.from(selectedProduct.linesByDay.entries())
          .filter(([day, line]) => selectedDays.includes(day) && (!!line.external_id || !!line.id))

        await Promise.all(lines.map(async ([day, line]) => {
          // resumeFrom is this day's first delivery after the paused period. The
          // freeze ends the day before it, which is always after the start, so a
          // one-delivery pause still satisfies "no same-day range".
          const resumeFrom = (availableDatesByDay[day] || [])
            .find(d => d > effectivePauseUntil)

          if (!resumeFrom) {
            throw new Error(`We could not schedule ${getDayName(day)} to start again.`)
          }

          await subscriptionService.freezeLine({
            subscription_line_id: line.id,
            effective_from: effectiveChangeFrom,
            effective_to: addDaysToDateString(resumeFrom, -1),
            also_pausing: lines.map(([, other]) => other.id).filter(id => id !== line.id),
          }, CHECKS_OUTCOME)
        }))

        await onSuccess?.()
        onClose()
        return
      }

      await Promise.all(perDaySchedule.map(({ day, lineId, chargebeeId, quantity, first, last }) => {
        if (!first) {
          return Promise.reject(new Error('This change could not be scheduled. Please reload and try again.'))
        }

        // A day the product is already on is amended; a day it isn't on is a
        // new line, which is why this dialog needs the price list at all.
        if (lineId) {
          // A removal keeps the plain path: the backend ends the line outright
          // rather than writing an amendment, so there is nothing to merge.
          if (quantity === 0) {
            return subscriptionService.createLineAmendment({
              subscription_line_id: lineId,
              quantity,
              effective_from: first,
            }, CHECKS_OUTCOME)
          }

          // Fold this change into whatever else is already pending on the line,
          // so CRM ends up with one tidy, non-overlapping set rather than
          // changes stacked on top of each other.
          const line = selectedProduct.linesByDay.get(day)
          // Trimmed to the line's own dates, so an older change that already
          // ran past its end isn't written back unchanged (DF-389).
          const ops = clampOpsToLine(
            planAmendments(
              intervalsForLine(lineId),
              { quantity, from: first, to: last || null },
              line?.quantity ?? 0
            ),
            line?.start_date ? line.start_date.slice(0, 10) : null,
            line?.end_date ? line.end_date.slice(0, 10) : null
          )

          return ops.reduce<Promise<void>>((chain, op) => chain.then(async () => {
            if (op.type === 'delete') {
              await subscriptionService.cancelAmendment(op.id, CHECKS_OUTCOME)
              return
            }
            await subscriptionService.createLineAmendment({
              subscription_line_id: lineId,
              quantity: op.quantity,
              effective_from: op.from,
              ...(op.to ? { effective_to: op.to } : {}),
              ...(op.type === 'update' ? { amendment_id: op.id } : { create_new: true }),
            }, CHECKS_OUTCOME)
          }), Promise.resolve())
        }

        if (!chargebeeId) {
          return Promise.reject(new Error(`${getDayName(day)} isn't available for this product.`))
        }

        return subscriptionService.createSubscriptionLine({
          subscription_id: productSubscriptionId,
          product_id: selectedProduct.productId,
          delivery_day: day,
          quantity,
          start_date: first,
          ...(last ? { end_date: last } : {}),
          chargebee_id: chargebeeId,
          product_name: selectedProduct.name,
        }, CHECKS_OUTCOME)
      }))

      // Awaited so "Saving..." stays until the refreshed order is on screen.
      await onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to submit changes:', err)

      // A save that timed out or lost its connection may still have reached
      // CRM (DF-385), so "try again" could double it up. Ask them to refresh
      // and check first.
      if (isUnknownOutcome(err)) {
        setError('We couldn’t confirm whether your product was added. Please refresh to check your delivery before trying again.')
        modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      let errorMessage = 'Failed to save changes. Please try again.'
      const apiError = err as { data?: { errors?: Record<string, string[]> }, message?: string }

      if (apiError?.data?.errors) {
        const messages: string[] = []
        for (const value of Object.values(apiError.data.errors)) {
          if (Array.isArray(value)) messages.push(...value)
        }
        if (messages.length > 0) errorMessage = messages.join(' ')
      } else if (apiError?.message) {
        errorMessage = apiError.message
      }

      setError(errorMessage)
      modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose()
  }

  if (!isOpen) return null

  const headingProduct = selectedProduct?.name ?? null

  /* The product row acts as the header once a product is chosen: it names the
     product, its price and what the customer currently gets. */
  // The chosen product, shown as the same card the chooser used, minus its
  // edit button: the customer has already picked it, so the card is a
  // reminder of what they are changing, not another thing to click.
  const productHeader = selectedProduct ? (() => {
    const changes = scheduledChangesByProduct?.get(selectedProduct.productId) ?? []
    const isStarting = changes.some(c => c.kind === 'starting')

    const annotations: ProductAnnotation[] = []
    const standing = describeStandingOrder(selectedProduct)
    if (!isStarting && standing) {
      annotations.push({ kind: 'standing', text: standing })
    }
    for (const change of changes) {
      annotations.push({
        kind: change.kind === 'ending' ? 'ending' : 'change',
        text: change.text,
      })
    }

    return (
      <SubscriptionProductCard
        name={selectedProduct.name}
        image={selectedProduct.image}
        unitPrice={selectedProduct.unitPrice}
        // Card-initiated: the card already shows the annotations, so the dialog
        // header stays clean and just names the product being changed.
        annotations={initialIntent ? [] : annotations}
      />
    )
  })() : null

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container add-product-modal" role="dialog" aria-modal="true">
        {/* Once a product is chosen its row is the header, so the title bar
            only appears on the picker. */}
        {step === 'product' && (
          <div className="modal-header">
            <h2>
              {step === 'product' || !headingProduct
                ? 'Change your deliveries'
                : `Change ${headingProduct}`}
            </h2>
            <button className="modal-close-floating" onClick={onClose} aria-label="Close modal">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {step !== 'product' && (
          <button
            type="button"
            className="modal-close-floating"
            onClick={onClose}
            aria-label="Close"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div className="modal-content" ref={modalContentRef}>
          {step === 'product' ? (
            <div className="add-product-step1">
              {products.length === 0 ? (
                <p className="add-product-empty">You have no products to change.</p>
              ) : (
                <>
                  <div className="change-product-list">
                    {products.map(product => {
                      const changes = scheduledChangesByProduct?.get(product.productId) ?? []
                      const isStarting = changes.some(c => c.kind === 'starting')

                      // Every card is annotated. The standing order IS
                      // information (what you get, on which days), so a card
                      // that showed nothing would read as "no data". It is
                      // suppressed only while the product is upcoming, where
                      // "you get 2 every Monday" would contradict the "starts
                      // on..." line beneath it.
                      const annotations: ProductAnnotation[] = []
                      const standing = describeStandingOrder(product)
                      if (!isStarting && standing) {
                        annotations.push({ kind: 'standing', text: standing })
                      }
                      for (const change of changes) {
                        annotations.push({
                          kind: change.kind === 'ending' ? 'ending' : 'change',
                          text: change.text,
                        })
                      }

                      return (
                        <SubscriptionProductCard
                          key={product.productId}
                          name={product.name}
                          image={product.image}
                          unitPrice={product.unitPrice}
                          annotations={annotations}
                          showEdit
                          onEdit={() => handleSelectProduct(product)}
                          onViewDetail={onViewProduct
                            ? () => onViewProduct(product.productId, product.name, product.unitPrice ?? 0)
                            : undefined}
                        />
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          ) : step === 'intent' ? (
            <div className="add-product-step2">
              {productHeader}

              {scheduledChanges.length > 0 && (
                <div className="sun-pending-changes">
                  <p className="sun-pending-changes-title">Already scheduled changes</p>
                  {scheduledChanges.map(change => (
                    <div
                      className="sun-pending-change"
                      key={`${change.kind}-${change.day}-${change.lineId}`}
                    >
                      <span>{change.text}</span>
                      {onCancelChange && (
                        <button
                          type="button"
                          className="sun-pending-cancel"
                          onClick={() => onCancelChange(change)}
                        >
                          <MdUndo aria-hidden="true" />
                          Cancel
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <p className="field-label">Schedule a change</p>

              {/* The two ordinary choices side by side; removing sits below as
                  a link, since it is permanent and much the rarer one. */}
              <div className="sun-intent-list">
                {([
                  { key: 'quantity', icon: <MdTune />, title: 'Change quantities', detail: 'Have more or fewer on a day, for a while or from now on.' },
                  { key: 'pause', icon: <MdPauseCircleOutline />, title: 'Pause this item', detail: 'Stop it for a set period, then start again automatically.' },
                ] as { key: Intent, icon: React.ReactNode, title: string, detail: string }[]).map(option => (
                  <button
                    key={option.key}
                    type="button"
                    className={`sun-intent-option is-${option.key}`}
                    onClick={() => {
                      setIntent(option.key)
                      setStep(option.key === 'quantity' ? 'quantity' : 'dates')
                    }}
                  >
                    <span className="sun-intent-icon" aria-hidden="true">{option.icon}</span>
                    <span className="sun-intent-text">
                      <span className="sun-intent-title">{option.title}</span>
                      <span className="sun-intent-detail">{option.detail}</span>
                    </span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="sun-zero-remove"
                onClick={() => { setIntent('remove'); setStep('dates') }}
              >
                <MdDeleteOutline aria-hidden="true" />
                Remove this item altogether
              </button>
            </div>
          ) : step === 'quantity' ? (
            <div className="add-product-step2">
              {productHeader}

              {restrictedToDays && (
                <p className="sun-day-restriction">
                  <MdInfoOutline aria-hidden="true" />
                  <span>
                    {selectedProduct?.name} can only be delivered on {restrictedToDays}.
                  </span>
                </p>
              )}

              <p className="field-label">How many on each day?</p>

              <div className="add-product-day-list">
                {productDays.map(day => {
                  const qty = quantityForDay(day)
                  const was = currentQuantityForDay(day)
                  const line = selectedProduct?.linesByDay.get(day)
                  const pending = line ? pendingForLine(line) : []
                  return (
                    <div key={day} className={`add-product-day-row ${qty === 0 ? 'disabled' : ''}`}>
                      <div className="add-product-day-info">
                        <span className="add-product-day-name">{getDayName(day)}</span>
                        <span className="add-product-day-note">
                          {line ? `Currently ${was}` : 'Not on your order yet'}
                        </span>
                        {pending.map(a => (
                          <span className="add-product-day-note" key={a.amendment_id}>
                            Pending: {a.new_quantity} from{' '}
                            {formatOrdinalDate(a.effective_from.slice(0, 10))}
                            {a.effective_to && ` to ${formatOrdinalDate(a.effective_to.slice(0, 10))}`}
                          </span>
                        ))}
                      </div>
                      <div className="quantity-control">
                        <button
                          className="qty-btn"
                          onClick={() => setDayQty(day, qty - 1)}
                          disabled={submitting || qty <= 0}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          className="qty-input"
                          min={0}
                          value={qty}
                          disabled={submitting}
                          onChange={(e) => setDayQty(day, parseInt(e.target.value) || 0)}
                        />
                        <button
                          className="qty-btn"
                          onClick={() => setDayQty(day, qty + 1)}
                          disabled={submitting}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {zeroingEverything ? (
                <div className="sun-zero-choice">
                  <p className="sun-zero-choice-title">
                    Looks like you&rsquo;d like to stop this item
                  </p>

                  <button
                    type="button"
                    className="sun-intent-option is-pause"
                    onClick={() => { setIntent('pause'); setStep('dates') }}
                  >
                    <MdPauseCircleOutline className="sun-intent-icon" aria-hidden="true" />
                    <span className="sun-intent-text">
                      <span className="sun-intent-title">Yes, pause it for a while</span>
                      <span className="sun-intent-detail">
                        It stops between the dates you choose, then starts again on its own.
                      </span>
                    </span>
                  </button>

                  {/* A link rather than a second card: removing is permanent
                      and much the rarer choice, so it should not sit level
                      with pausing as though the two were equally likely. */}
                  <button
                    type="button"
                    className="sun-zero-remove"
                    onClick={() => { setIntent('remove'); setStep('dates') }}
                  >
                    <MdDeleteOutline aria-hidden="true" />
                    No, remove it altogether
                  </button>
                </div>
              ) : null}

              {wouldRemoveEverything && (
                <p className="field-error">
                  You’ll need to keep at least one item in your subscription. To stop deliveries
                  temporarily, you can pause them in <strong>Delivery Management</strong>. To cancel
                  your subscription, please call our Customer Service team on <strong>0330 555 5588</strong>.
                </p>
              )}
            </div>
          ) : (
            <div className="add-product-step3">
              {productHeader}
              {/* No lead-in for a pause: the dates and the summary box below
                  them already say what happens, and the header lists the days
                  it covers. */}
              {intent === 'remove' && (
                <p className="field-hint">
                  {selectedProduct?.name} will stop from the date you choose, so that
                  delivery and any after it won&apos;t come.
                </p>
              )}

              {/* When the product is on more than one day, let the customer
                  pause or stop just some of them; every day is ticked by default. */}
              {(intent === 'pause' || intent === 'remove') && productDeliveryDays.length > 1 && (
                <fieldset className="change-day-picker">
                  <legend className="field-label">Which days?</legend>
                  <div className="change-day-options">
                    {productDeliveryDays.map(day => (
                      <label key={day} className="change-day-option">
                        <input
                          type="checkbox"
                          checked={selectedDays.includes(day)}
                          onChange={() => toggleDay(day)}
                          disabled={submitting}
                        />
                        {getDayName(day)}s
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              <label className="field-label" htmlFor="change-items-from">
                {intent === 'remove'
                  ? 'When would you like it to stop?'
                  : intent === 'pause'
                    ? 'When would you like the pause to start?'
                    : 'When would you like this change to start?'}
              </label>
              {unionDates.length === 0 ? (
                <>
                  <p className="field-error">No available dates in the next {HORIZON_WEEKS} weeks.</p>
                  {intent !== 'remove' && endNote && <p className="field-hint">{endNote}</p>}
                </>
              ) : (
                <>
                  <select
                    id="change-items-from"
                    className="add-product-date-select"
                    value={effectiveChangeFrom}
                    onChange={(e) => setChangeFrom(e.target.value)}
                  >
                    {unionDates.map(d => (
                      <option key={d} value={d}>{formatOrdinalDate(d)}</option>
                    ))}
                  </select>

                  {changeDatePushed && (
                    <p className="field-hint">This is the earliest date this change can take effect.</p>
                  )}

                  {intent !== 'remove' && endNote && (
                    <p className="field-hint">{endNote}</p>
                  )}

                  {intent === 'pause' ? (
                    <>
                      <label className="field-label" htmlFor="change-items-until">Until</label>
                      <select
                        id="change-items-until"
                        className="add-product-date-select"
                        value={effectivePauseUntil}
                        onChange={(e) => setPauseUntil(e.target.value)}
                      >
                        {untilOptions.map(d => (
                          <option key={d} value={d}>{formatOrdinalDate(d)}</option>
                        ))}
                      </select>
                      <div className="add-product-start-summary sun-change-preview is-pause">
                        <MdPauseCircleOutline className="sun-change-preview-icon" aria-hidden="true" />
                        <div className="add-product-schedule">
                          <div className="add-product-schedule-line">
                            Paused from {shortDate(effectiveChangeFrom)}
                            {effectivePauseUntil === effectiveChangeFrom
                              ? ' for that delivery only'
                              : ` until ${shortDate(effectivePauseUntil)}`}
                            , then back to normal.
                          </div>
                        </div>
                      </div>
                    </>
                  ) : intent === 'remove' ? (
                    // Don't confirm a removal we won't allow: showing "then it
                    // stops" and then a blocking error underneath reads as a
                    // contradiction. When this is the last item, show the reason
                    // instead of the confirmation (FEDEV-599, Laura's feedback).
                    wouldRemoveEverything ? (
                      <div className="add-product-start-summary sun-change-preview is-blocked">
                        <MdInfoOutline className="sun-change-preview-icon" aria-hidden="true" />
                        <div className="add-product-schedule">
                          <div className="add-product-schedule-line">
                            You’ll need to keep at least one item in your subscription. To stop
                            deliveries temporarily, you can pause them in <strong>Delivery Management</strong>.
                            To cancel your subscription, please call our Customer Service team on{' '}
                            <strong>0330 555 5588</strong>.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="add-product-start-summary sun-change-preview is-remove">
                        <MdDeleteOutline className="sun-change-preview-icon" aria-hidden="true" />
                        <div className="add-product-schedule">
                          <div className="add-product-schedule-line">
                            You won’t receive a delivery on {shortDate(effectiveChangeFrom)} or
                            any future deliveries.
                          </div>
                        </div>
                      </div>
                    )
                  ) : durationLocked ? (
                    <p className="field-hint">
                      Removing a product is permanent, so it can’t be given an end date. To pause
                      instead, close this and use <strong>Pause</strong> on the delivery card.
                    </p>
                  ) : (
                    <>
                      <label className="field-label" htmlFor="change-items-until">Until</label>
                      <select
                        id="change-items-until"
                        className="add-product-date-select"
                        value={effectiveChangeUntil}
                        onChange={(e) => setChangeUntil(e.target.value)}
                      >
                        <option value="">Until further notice</option>
                        {untilOptions.map(d => (
                          <option key={d} value={d}>{formatOrdinalDate(d)}</option>
                        ))}
                      </select>
                    </>
                  )}

                  {/* The outcome, after the controls that shape it: read the
                      dates, then see what they add up to. No lead-in, since
                      every line already says what changes and when. */}
                  {intent === 'quantity' && (
                    <div className="add-product-start-summary sun-change-preview">
                      <MdTune className="sun-change-preview-icon" aria-hidden="true" />
                      <div className="add-product-schedule">
                        {perDaySchedule.map(({ day, quantity, was, first, last }) => {
                          // No delivery on or after the start for this day: say
                          // so (saving is blocked) rather than start earlier.
                          if (!first) {
                            return (
                              <div className="add-product-schedule-line" key={day}>
                                No {getDayName(day)} delivery available from {shortDate(effectiveChangeFrom)}
                              </div>
                            )
                          }
                          const when = last === first
                            ? `${shortDate(first)} only`
                            : last
                              ? `${shortDate(first)} until ${shortDate(last)}`
                              : `from ${shortDate(first)}`

                          // A day that had none of this product is an addition,
                          // not a change from zero. Each line opens with the
                          // verb, so the box reads as a list of actions.
                          const what = quantity === 0
                            ? 'Stopping'
                            : was === 0
                              ? `Adding ${quantity} a week`
                              : `Change to ${quantity} instead of ${was}`

                          return (
                            <div className="add-product-schedule-line" key={day}>
                              {what} on {getDayName(day)}s, {when}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* DF-478: a change starting on a date whose delivery has
                      moved (Christmas) says when it actually comes. */}
                  {intent === 'quantity' && perDaySchedule
                    .map(({ first }) => (first ? movedDateNote(first, moves.targets, moves.periods, longDate) : null))
                    .filter((note, i, all): note is string => !!note && all.indexOf(note) === i)
                    .map(note => (
                      <p key={note} className="schedule-move-note" role="note">{note}</p>
                    ))}
                </>
              )}

              {/* Removal confirmation (BUG-3): removing is permanent, skipping
                  is the holiday flow. Shown after the first Save click. */}
              {confirmingRemovals && removedDays.length > 0 && (
                <div className="removal-confirm" role="alert">
                  <p className="removal-confirm-title">
                    Remove {selectedProduct?.name} from your {removedDays.map(getDayName).join(' and ')} deliveries?
                  </p>
                  <p>
                    This takes it off <strong>all future deliveries</strong> from{' '}
                    {formatDate(effectiveChangeFrom)}, not just this one.
                  </p>
                  <p>
                    Only want to pause a delivery? Close this window and use{' '}
                    <strong>Pause</strong> on the delivery card instead.
                  </p>
                </div>
              )}

              {error && isCutoffErrorMessage(error) && cutoffs ? (
                <CutoffMessage variant="error" minDate={minDate} />
              ) : error ? (
                <p className="field-error field-error--submit">{error}</p>
              ) : null}
            </div>
          )}
        </div>

        {/* A lone Cancel belongs on the left at its own width, not stretched
            across the footer where a primary action would sit. */}
        <div className={`modal-footer add-product-footer${step === 'intent' || step === 'product' ? ' is-start' : ''}`}>
          {step === 'product' ? (
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
          ) : step === 'intent' ? (
            <button
              className="btn-cancel"
              onClick={() => (initialProductId ? onClose() : setStep('product'))}
            >
              {initialProductId ? 'Cancel' : 'Back'}
            </button>
          ) : step === 'quantity' ? (
            <>
              <button className="btn-cancel" onClick={() => setStep('intent')}>Back</button>
              <button
                className="btn-confirm"
                onClick={() => setStep('dates')}
                // Zeroing every day routes through the pause-or-remove choice
                // above instead, so Next would skip the question.
                disabled={changedDays.length === 0 || wouldRemoveEverything || zeroingEverything}
              >
                Next
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-cancel"
                onClick={() => initialIntent
                  ? onClose()
                  : setStep(intent === 'quantity' ? 'quantity' : 'intent')}
                disabled={submitting}
              >
                {initialIntent ? 'Cancel' : 'Back'}
              </button>
              <button
                className="btn-confirm"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
              >
                {submitting ? (
                  <>
                    <span className="btn-spinner" />
                    Saving...
                  </>
                ) : confirmingRemovals && removedDays.length > 0 ? (
                  'Yes, remove it'
                ) : (
                  'Confirm'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChangeItemsModal
